import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useGeneralRefetchOverride } from "src/queries/general.queries";
import { QueryNames } from "src/queries/queries.constant";
import { getApiLanguage } from "src/services/commonLanguageHeader";
import {
  createWarehouseResource,
  deleteWarehouseResource,
  getAllWarehousesByDistributor,
  getWarehouseChildLookup,
  getWarehouseChildren,
  getWarehouseLookup,
  getWarehouseTypeLookup,
  updateWarehouseResource,
  type WarehouseListParams,
  type WarehouseLookupParams,
  type WarehouseResourcePayload,
  type WarehouseResourceType,
} from "src/services/warehouseService";

const getResourceQueryKey = (resource: WarehouseResourceType) =>
  QueryNames.warehouseHierarchy[resource];

const getResourceLookupQueryKey = (
  resource: Exclude<WarehouseResourceType, "warehouse">,
) => QueryNames.warehouseHierarchyLookup[resource];

const invalidateWarehouseQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
) => {
  queryClient.invalidateQueries({
    queryKey: QueryNames.warehouseHierarchy.warehouse,
  });
  queryClient.invalidateQueries({
    queryKey: QueryNames.warehouseHierarchy.area,
  });
  queryClient.invalidateQueries({
    queryKey: QueryNames.warehouseHierarchy.lane,
  });
  queryClient.invalidateQueries({
    queryKey: QueryNames.warehouseHierarchy.shelf,
  });
  queryClient.invalidateQueries({
    queryKey: QueryNames.warehouseHierarchy.position,
  });
  queryClient.invalidateQueries({
    queryKey: QueryNames.warehouseLookup,
  });
  queryClient.invalidateQueries({
    queryKey: QueryNames.warehouseHierarchyLookup.area,
  });
  queryClient.invalidateQueries({
    queryKey: QueryNames.warehouseHierarchyLookup.lane,
  });
  queryClient.invalidateQueries({
    queryKey: QueryNames.warehouseHierarchyLookup.shelf,
  });
};

export const useQueryGetWarehouses = (
  params: WarehouseListParams,
  options: { enabled?: boolean } & Record<string, unknown> = {},
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  const enabled = options.enabled !== false;

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.warehouseHierarchy.warehouse, params, language],
      queryFn: () => getAllWarehousesByDistributor(params, language),
      retry: false,
      ...options,
      enabled,
    }),
    enabled,
  );
};

export const useQueryGetWarehouseChildren = (
  resource: Exclude<WarehouseResourceType, "warehouse">,
  parentId?: string | number,
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: [...getResourceQueryKey(resource), parentId, language],
    queryFn: () =>
      getWarehouseChildren(resource, parentId as string | number, language),
    enabled: parentId !== undefined && parentId !== "",
    retry: false,
  });
};

export const useQueryGetWarehouseTypes = (enabled = true) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: [...QueryNames.warehouseTypes, language],
    queryFn: () => getWarehouseTypeLookup(language),
    enabled,
    retry: false,
  });
};

export const useQueryGetWarehouseLookup = (
  params: WarehouseLookupParams,
  enabled = true,
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: [...QueryNames.warehouseLookup, params, language],
    queryFn: () => getWarehouseLookup(params, language),
    enabled,
    retry: false,
  });
};

export const useQueryGetWarehouseChildLookup = (
  resource: Exclude<WarehouseResourceType, "warehouse">,
  parentId?: string | number,
  enabled = true,
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: [
      ...getResourceLookupQueryKey(resource),
      parentId,
      language,
    ],
    queryFn: () =>
      getWarehouseChildLookup(
        resource,
        parentId as string | number,
        language,
      ),
    enabled: enabled && parentId !== undefined && parentId !== "",
    retry: false,
  });
};

export const useMutationCreateWarehouseResource = (
  resource: WarehouseResourceType,
) => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (payload: WarehouseResourcePayload) =>
      createWarehouseResource(resource, payload, language),
    onSuccess: () => {
      invalidateWarehouseQueries(queryClient);
    },
  });
};

export const useMutationUpdateWarehouseResource = (
  resource: WarehouseResourceType,
) => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (payload: WarehouseResourcePayload) =>
      updateWarehouseResource(resource, payload, language),
    onSuccess: () => {
      invalidateWarehouseQueries(queryClient);
    },
  });
};

export const useMutationDeleteWarehouseResource = (
  resource: WarehouseResourceType,
) => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (id: string | number) =>
      deleteWarehouseResource(resource, id, language),
    onSuccess: () => {
      invalidateWarehouseQueries(queryClient);
    },
  });
};
